import React, { useContext, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  IObjektVy,
  BusinessCollectionCompType,
} from '../../../../../Types/ObjektVy';
import ObjektVySida from '../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import { ObjektVyKort } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import ObjektVy from '../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import BusinessDataForm, {
  BusinessDataFormContext,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import Attribut from '../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import { collectionCompToObjektVy } from '../../../_Common/Components/BusinessCollectionComponents/BusinessCollectionUtils';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { useSelectedDetailQuery } from '../../../../../Components/QueryComponents/SelectedDetailQuery/useSelectedDetailQuery';
import clientAvenyModel from '../../../../../Model/clientAvenyModel';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import UpdateMutation from './Mutations/OversiktRapporterUpdateMutation.gql';
import AddMutation from './Mutations/OversiktRapporterAddMutation.gql';
import OversiktRapporterQueryDoc from './Queries/OversiktRapporterQuery.gql';
import {
  OversiktRapporterQuery,
  OversiktRapporterQueryVariables,
  OversiktRapporterQuery_db_brevLayouts,
  OversiktRapporterQuery_node_TEDSYSReportNew,
  OversiktRapporterQuery_node_TEDSYSReportNew__Associations,
} from './Queries/_QueryTypes/OversiktRapporterQuery';
import {
  OversiktRapporterUpdateMutation,
  OversiktRapporterUpdateMutationVariables,
} from './Mutations/_QueryTypes/OversiktRapporterUpdateMutation';
import {
  OversiktRapporterAddMutation,
  OversiktRapporterAddMutationVariables,
} from './Mutations/_QueryTypes/OversiktRapporterAddMutation';
import {
  IEditMenuAction,
  IFieldValidations,
  IFormData,
  IValidateFn,
  defaultEditMenuCancelButton,
  defaultEditMenuSaveButton,
  removeIdsFromNewAssociations,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import EDAvenyProgress from '../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { BusinessDataFormRapportMall } from './Components/BusinessDataFormRapportMall';
import { EDAutoCompleteEdit } from '../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import {
  IEditorReportParams,
  handlePreviewClick,
} from './Utils/OversiktRapporterUtils';
import {
  definedSelectedId,
  selectedId,
} from '../../../../../Components/MasterDetail/MasterDetailUtils';
import { RutinSettingsContext } from '../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import EDCheckbox from '../../../../../BaseComponents/Checkboxes/EDCheckbox';
import {
  AssociationList,
  IAssociationListConfig,
} from '../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import { GridContext } from '../../../../GridContext';
import { defaultBusinessCollectionMenuItem } from '../../../_Common/Components/BusinessCollectionComponents/BusinessCollectionMenu/Utils/BusinessCollectionMenuUtils';
import { TSelectedDetail } from '../../../../../Types/MasterDetailTypes';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import shortid from 'shortid';
import { ObjektLank } from '../../../_Common/Components/BusinessCollectionComponents/ObjektLank/ObjektLank';
import { useLocation } from 'react-router';
import AttributGrupp from '../../../_Common/Components/BusinessCollectionComponents/AttributGrupp/AttributGrupp';
import { ModelContext } from '../../../../../Model/ModelContext/ModelContext';
import {
  IModelTreeNode,
  ModelTreeProvider,
} from '../../../../../BaseComponents/Rapporteditor/ModelTreeContext';
import reportEditorInfoFieldsQuery from './Queries/ReportEditorInfoFieldsQuery.gql';
import { ReportEditorInfoFieldsQuery } from './Queries/_QueryTypes/ReportEditorInfoFieldsQuery';
import { RapportmallRootProperties } from './Components/RapportmallRootProperties';

const oversiktRapporterModel: IClientBusinessModel =
  clientAvenyModel.SYS.BusinessModels.SYSReportNew;

const ParamsGrid: React.FC<{ id: TSelectedDetail }> = props => {
  const formContext = useContext(BusinessDataFormContext);
  const formContextUpdate = formContext?.update;

  const paramsConfig: IAssociationListConfig = useMemo(
    () => ({
      inCard: true,
      defaultVisningstyp: 'tabell',
      integrated: true,
      showDeleteIcon: true,
      hideDefaultFunktionsMenyItems: true,
      customDefaultColumns: [
        { attributeDef: 'NAMN' },
        {
          attributeDef: 'TYP',
        },
        { attributeDef: 'KRAVS' },
        { attributeDef: 'PROPOSE_VALUES' },
        { attributeDef: 'Spara' },
      ],
      businessCollectionMenuItems: [
        {
          ...defaultBusinessCollectionMenuItem.Add,
          onClick: params => {
            formContextUpdate?.([
              {
                operation: 'Add_Values',
                id: `${NEWLY_ADDED_ID_PREFIX_FOR_UPDATE}Params_${shortid.generate()}`,
                associationName: 'Params',
              },
            ]);
          },
        },
      ],
      highlightedBusinessCollectionMenuItem:
        defaultBusinessCollectionMenuItem.Add.id,
      useFunctionMenuItems: (input, customData) => {
        const deleteAction = () => {
          if (input.id) {
            formContextUpdate?.([
              {
                operation: 'Delete_Values',
                id: input.id,
                associationName: 'Params',
              },
            ]);
          }
        };
        return [
          {
            caption: 'Ta bort',
            id: 'delete',
            onClick: deleteAction,
            iconType: 'delete',
          },
        ];
      },
    }),
    [formContextUpdate]
  );

  return (
    <GridContext.Provider value={{ inGrid: true }}>
      <AssociationList
        associationName="Params"
        associationPath="Params"
        collectionName={'SYSReportNew'}
        ownId={definedSelectedId(props.id ?? '')}
        config={paramsConfig}
      />
    </GridContext.Provider>
  );
};

const OversiktRapporter: BusinessCollectionCompType = props => {
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const { activeRoutine } = useContext(RutinSettingsContext);
  const modelContext = useContext(ModelContext);
  const location = useLocation();

  const [previewParams, setPreviewParams] = useState<IEditorReportParams>({
    DigiSign: 'Nej',
    Layout: null,
  });

  const { nodeData, loading, data } = useSelectedDetailQuery<
    OversiktRapporterQuery,
    OversiktRapporterQuery_node_TEDSYSReportNew,
    OversiktRapporterQueryVariables
  >(OversiktRapporterQueryDoc, props.id, {
    queryOptions: {
      variables: {
        dbName: activeDatabase ?? '',
        id: selectedId(props.id) ?? '',
      },
    },
  });

  const [updateMutation, updateResult] = useMutation<
    OversiktRapporterUpdateMutation,
    OversiktRapporterUpdateMutationVariables
  >(UpdateMutation);

  const [addMutation, addResult] = useMutation<
    OversiktRapporterAddMutation,
    OversiktRapporterAddMutationVariables
  >(AddMutation);

  const mutationFunction = (values: IFormData) => {
    const newValues = removeIdsFromNewAssociations(values);
    return updateMutation({
      variables: {
        db: activeDatabase || '',
        values: {
          id: nodeData?.id ?? '',
          ...newValues,
          _version: nodeData?._version ?? 1,
        },
      },
    });
  };

  const addFunction = (values: IFormData) => {
    return addMutation({
      variables: {
        db: activeDatabase || '',
        values: {
          SUBSYSTEMCODE: values.SUBSYSTEMCODE ?? '',
          ShortCollectionName: values.ShortCollectionName ?? '',
          REPORTNAME: values.REPORTNAME ?? '',
          _typ: values._typ ?? 'PDFREPORTNEW',
          ...values,
        },
      },
    });
  };

  const previewButton: IEditMenuAction = useMemo(() => {
    return {
      id: 'preview',
      label: 'Förhandsgranska',
      onClick: handlePreviewClick(
        activeDatabase || '',
        activeRoutine?.routine.rutinID,
        previewParams
      ),
      flat: true,
      isHidden: ({ businessDataFormContext }) =>
        businessDataFormContext.formState.currentValues?._typ !==
        'PDFREPORTNEW',
      icon: 'eye_outline',
    };
  }, [activeDatabase, activeRoutine?.routine.rutinID, previewParams]);

  const validateReport: IValidateFn<
    OversiktRapporterQuery_node_TEDSYSReportNew
  > = ({ currentValues }): IFieldValidations => {
    let errors: IFieldValidations = [];
    errors.push({
      type: 'RequiredValueHint',
      fieldName: 'SUBSYSTEMCODE',
    });
    if (!currentValues?.SUBSYSTEMCODE) {
      errors.push({
        type: 'MissingValue',
        fieldName: 'SUBSYSTEMCODE',
      });
    }
    errors.push({
      type: 'RequiredValueHint',
      fieldName: 'ShortCollectionName',
    });
    if (!currentValues?.ShortCollectionName) {
      errors.push({
        type: 'MissingValue',
        fieldName: 'ShortCollectionName',
      });
    }
    if (!currentValues?.REPORTNAME) {
      errors.push({
        type: 'MissingValue',
        fieldName: 'REPORTNAME',
      });
    }
    return errors;
  };
  const initialActivePageStr = new URLSearchParams(location.search).get(
    'initialPage'
  );
  const initialActivePage =
    initialActivePageStr != null ? Number(initialActivePageStr) : undefined;
  const initialActivePages =
    initialActivePage != null && !isNaN(initialActivePage)
      ? [initialActivePage]
      : undefined;
  const { data: infofieldsData } = useQuery<ReportEditorInfoFieldsQuery>(
    reportEditorInfoFieldsQuery
  );
  const infoFields: IModelTreeNode[] = useMemo(
    () =>
      (infofieldsData?.rtn?.SYSReportEditorInfoFields || []).map(field => ({
        name: field.name ?? '',
        path: field.name ?? '',
        displayName: field.caption ?? '',
        exportable: true,
      })),
    [infofieldsData]
  );

  return (
    <BusinessDataForm<OversiktRapporterQuery_node_TEDSYSReportNew>
      isNew={props.isNew}
      data={nodeData}
      dataLoading={loading}
      model={oversiktRapporterModel}
      updateMutation={mutationFunction}
      addMutation={addFunction}
      fullScreen={props.config?.fullPage ?? false}
      mutationError={updateResult.error || addResult.error}
      mutationLoading={updateResult.loading || addResult.loading}
      validateFns={[validateReport as IValidateFn]}
      newFormContext
      editMenuActions={[
        previewButton,
        defaultEditMenuCancelButton,
        defaultEditMenuSaveButton,
      ]}
      initialValue={{ _typ: 'PDFREPORTNEW', PdfJson: '{"Body":[]}' }}
      isEditing
    >
      <BusinessDataFormContext.Consumer>
        {formContext => {
          if (formContext?.formState.loading) {
            return <EDAvenyProgress />;
          }

          if (formContext?.formState.currentValues?._typ === 'PDFREPORTNEW') {
            const subsystemCodes = Object.keys(clientAvenyModel);
            const subsystemCode =
              formContext?.formState.currentValues?.SUBSYSTEMCODE ?? '';
            const businessModels =
              clientAvenyModel[subsystemCode]?.BusinessModels;
            const collections = businessModels
              ? Object.keys(businessModels)
                  .filter(
                    collectionName =>
                      businessModels[collectionName].Properties.has(
                        'USER_DEF_REPORT_ROUTINE'
                      ) &&
                      !businessModels[collectionName].Properties.has(
                        'NO_EXPORT'
                      )
                  )
                  .map(collectionName => ({
                    name:
                      businessModels[collectionName].RootType.POLabel ||
                      collectionName,
                    value: collectionName,
                  }))
                  .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
              : [];

            return (
              <ModelTreeProvider
                ShortCollectionName={
                  formContext?.formState.currentValues?.ShortCollectionName
                }
                infoFields={infoFields}
                Params={
                  formContext?.formState.currentValues?._Associations?.Params
                }
                avenyCollectionMap={modelContext.avenyCollectionMap}
              >
                <ObjektVy
                  nodeLoading={loading}
                  editImplemented
                  initialActivePages={initialActivePages}
                >
                  <ObjektVySida
                    sidNr={1}
                    caption="Rapportmall"
                    collectionName={oversiktRapporterModel.CollectionName}
                  >
                    <BusinessDataFormRapportMall />
                  </ObjektVySida>
                  <ObjektVySida
                    sidNr={2}
                    caption="Inställningar"
                    collectionName={oversiktRapporterModel.CollectionName}
                  >
                    <AttributGrupp>
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew>
                        name="SUBSYSTEMCODE"
                        options={subsystemCodes.map(code => ({
                          name: clientAvenyModel[code].DisplayLabel || code,
                          value: code,
                        }))}
                      />
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew>
                        name="ShortCollectionName"
                        options={collections.map(collection => ({
                          name: collection.name,
                          value: collection.value,
                        }))}
                      />
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew> name="REPORTNAME" />
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew> name="DESCRIPTION" />
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew> name="BlankettID" />
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew> name="PageHeaderOption" />
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew> name="PageFooterOption" />
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew>
                        name="DigitalSignering"
                        customDisplayLabel="E-signering"
                      />
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew> name="USES_LAYOUT" />
                      <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew> name="VISIBILITY" />
                      <ObjektLank<OversiktRapporterQuery_node_TEDSYSReportNew__Associations>
                        associationName="Agare"
                        id={
                          nodeData &&
                          nodeData._Associations &&
                          nodeData._Associations.Agare
                            ? nodeData._Associations.Agare.id
                            : null
                        }
                      />
                    </AttributGrupp>
                    <ObjektVyKort>
                      <RapportmallRootProperties />
                    </ObjektVyKort>
                    <AttributGrupp caption="Förhandsgranskningsinställningar">
                      <EDCheckbox
                        label="E-signering"
                        checked={previewParams.DigiSign === 'Ja'}
                        onCheck={checked => {
                          setPreviewParams(prevParams => ({
                            ...prevParams,
                            DigiSign: checked ? 'Ja' : 'Nej',
                          }));
                        }}
                      />
                      <EDAutoCompleteEdit<
                        OversiktRapporterQuery_db_brevLayouts,
                        false
                      >
                        options={data?.db?.brevLayouts ?? []}
                        value={previewParams.Layout}
                        onChange={option => {
                          setPreviewParams(prevParams => ({
                            ...prevParams,
                            Layout: option,
                          }));
                        }}
                        label="Layout för förhandsgranskning"
                        getOptionLabel={option => option.BENAMNING ?? ''}
                        getOptionSelected={(option, value) =>
                          option.id === value.id
                        }
                        fullWidth
                      />
                    </AttributGrupp>
                    <ObjektVyKort caption="Parametrar">
                      <ParamsGrid id={props.id} />
                    </ObjektVyKort>
                  </ObjektVySida>
                </ObjektVy>
              </ModelTreeProvider>
            );
          } else {
            return (
              <ObjektVy nodeLoading={loading} editImplemented>
                <ObjektVySida
                  sidNr={1}
                  caption="Översikt Rapporter"
                  collectionName={oversiktRapporterModel.CollectionName}
                >
                  <ObjektVyKort>
                    <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew> name="_typ" />
                    <Attribut<OversiktRapporterQuery_node_TEDSYSReportNew> name="REPORTNAME" />
                  </ObjektVyKort>
                </ObjektVySida>
              </ObjektVy>
            );
          }
        }}
      </BusinessDataFormContext.Consumer>
    </BusinessDataForm>
  );
};

const oversiktRapporterVy = collectionCompToObjektVy(
  OversiktRapporter,
  'OversiktRapporter'
);
export const OversiktRapporterVyer: IObjektVy[] = [oversiktRapporterVy];
